import React, { useState, useEffect } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import EKYCCardsUnitsFPS from "./EKYCCardsUnitsFPS";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";

const EKYCCardsUnitsMandal = ({
  distMonth,
  distYear,
  distMonthName,
  distCode,
  distName,
}) => {
  const [loading, setLoading] = useState(false);
  const [schemeData, setSchemeData] = useState([]);
  const [afsoData, setAfsoData] = useState([]);
  const [renderContent, setRenderContent] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [schemeRes, afsoRes] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_URL}/api/ekyc-info/ekyc-card-units-scheme?distMonth=${distMonth}&distYear=${distYear}`
        ),
        fetch(
          `${process.env.REACT_APP_URL}/api/ekyc-info/ekyc-afso-card-units?distMonth=${distMonth}&distYear=${distYear}&distCode=${distCode}`
        ),
      ]);

      if (!schemeRes.ok) throw new Error("Failed to fetch Scheme Details");
      if (!afsoRes.ok) throw new Error("Failed to fetch Afso Details");

      setSchemeData(await schemeRes.json());
      setAfsoData(await afsoRes.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [distMonth, distYear, distCode]);

  

  const handleAfsoClick = (afso_code, afso_name_en) => {
    setRenderContent(
      <EKYCCardsUnitsFPS
        key={afso_code}
        distMonth={distMonth}
        distYear={distYear}
        distMonthName={distMonthName}
        distCode={distCode}
        distName={distName}
        afsoCode={afso_code}
        afsoName={afso_name_en}
      />
    );
  };

  const getSchemeTotals = (schemeId, fieldPrefix) =>
    afsoData.reduce((sum, item) => sum + Number(item[`${fieldPrefix}_${schemeId}`] || 0), 0);

  const verticalHeaders = [
    "Total<br/>Cards",
    "Total<br/>Units",
    "Fully<br/>e-KYC<br/>Completed<br/>RC",
    "Fully<br/>e-KYC<br/>Done<br/>Units",
    "Partial<br/>e-KYC<br/>Done<br/>RC",
    "Partial<br/>e-KYC<br/>Done<br/>Units",
    "Partial<br/>e-KYC<br/>Not<br/>Done<br/>Units",
    "e-KYC<br/>Not<br/>Done<br/>RC",
    "e-KYC<br/>Not<br/>Done<br/>Units",
  ];

  const handleDownloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("EKYC Report");

    const totalColumns = 2 + schemeData.length * verticalHeaders.length;

    // Title
    const titleRow = worksheet.addRow([
      `EKYC Card Unit Status in District: ${distName} for ${distMonthName}'${distYear} for Date: ${
        afsoData[0]?.data_date
          ? new Date(afsoData[0].data_date).toLocaleDateString("en-GB")
          : ""
      }`,
    ]);
    worksheet.mergeCells(1, 1, 1, totalColumns);
    titleRow.eachCell((cell) => {
      cell.font = { bold: true, size: 14 };
      cell.alignment = { horizontal: "center", vertical: "middle" };
    });

    // Header rows
    const headerRow1 = ["S.No", "Mandal"];
    schemeData.forEach((scheme) => {
      for (let i = 0; i < verticalHeaders.length; i++) {
        headerRow1.push(scheme.schemename);
      }
    });
    worksheet.addRow(headerRow1);

    const headerRow2 = ["", ""];
    schemeData.forEach(() => {
      headerRow2.push(...verticalHeaders.map((h) => h.replace(/<br\/>/g, "\n")));
    });
    worksheet.addRow(headerRow2);

    // Merge scheme header
    let colStart = 3;
    schemeData.forEach(() => {
      worksheet.mergeCells(2, colStart, 2, colStart + verticalHeaders.length - 1);
      colStart += verticalHeaders.length;
    });

    // Style headers
    worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
      if (rowNumber <= 3) {
        row.eachCell((cell) => {
          cell.font = { bold: true };
          cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      }
    });

    // Data rows
    afsoData.forEach((item, idx) => {
      const rowData = [
        idx + 1,
        item.afso_name_en,
        ...schemeData.flatMap((scheme) => [
          item[`total_rc_${scheme.schemeid}`],
          item[`total_units_${scheme.schemeid}`],
          item[`fully_ekyc_completed_rc_${scheme.schemeid}`],
          item[`fully_ekyc_done_units_${scheme.schemeid}`],
          item[`partial_ekyc_done_rc_${scheme.schemeid}`],
          item[`partial_ekyc_done_units_${scheme.schemeid}`],
          item[`partial_ekyc_not_done_units_${scheme.schemeid}`],
          item[`ekyc_not_done_rc_${scheme.schemeid}`],
          item[`ekyc_not_done_units_${scheme.schemeid}`],
        ]),
      ];
      worksheet.addRow(rowData);
    });

    // Total row
    const totalRowData = [
      "",
      "TOTAL",
      ...schemeData.flatMap((scheme) =>
        verticalHeaders.map((_, idx) => {
          const fieldMap = [
            "total_rc",
            "total_units",
            "fully_ekyc_completed_rc",
            "fully_ekyc_done_units",
            "partial_ekyc_done_rc",
            "partial_ekyc_done_units",
            "partial_ekyc_not_done_units",
            "ekyc_not_done_rc",
            "ekyc_not_done_units",
          ];
          return getSchemeTotals(scheme.schemeid, fieldMap[idx]);
        })
      ),
    ];
    const totalRow = worksheet.addRow(totalRowData);
    totalRow.font = { bold: true };

    worksheet.columns.forEach((col) => (col.width = 18));
    worksheet.views = [{ state: "frozen", ySplit: 3 }];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `EKYC_Mandal_Report_${distMonthName}_${distYear}.xlsx`);
  };

    if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex" }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <>
      {schemeData.length > 0 ? (
        <div style={{ justifyContent: "center"}}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              margin: "0 auto 20px auto",
            }}
          >
            <div style={{ width: "20px" }}></div>
            <div
              style={{
                overflowY: "auto",
                overflowX: "auto",
                width: "100%",
                margin: "0 auto",
                border: "1px solid #ccc",
              }}
            >
              <table
                width="100%"
                border="1"
                cellPadding="5"
                cellSpacing="0"
                style={{ minWidth: "100%", width: "max-content" }}
              >
                <thead style={{ position: "sticky", top: 0 }}>
                  <tr className="tableheader">
                    <th colSpan={2 + schemeData.length * verticalHeaders.length}>
                      EKYC Card Unit Status in District: {distName} for {distMonthName}'
                      {distYear} for Date:{" "}
                      {afsoData[0]?.data_date
                        ? new Date(afsoData[0].data_date).toLocaleDateString("en-GB")
                        : ""}
                    </th>
                  </tr>
                  <tr className="tableheader">
                    <th rowSpan={2}>S.No</th>
                    <th rowSpan={2}>Mandal</th>
                    {schemeData.map((scheme, idx) => (
                      <th key={idx} colSpan={verticalHeaders.length}>
                        {scheme.schemename}
                      </th>
                    ))}
                  </tr>
                  <tr className="tableheader">
                    {schemeData.map((scheme, idx) =>
                      verticalHeaders.map((header, hIdx) => (
                        <th
                          key={`${idx}-${hIdx}`}
                          style={{ textAlign: "left", whiteSpace: "pre-line" }}
                          dangerouslySetInnerHTML={{ __html: header }}
                        ></th>
                      ))
                    )}
                  </tr>
                </thead>
                <tbody>
                  {afsoData.map((item, idx) => (
                    <tr key={idx}>
                      <td style={{ textAlign: "center" }}>{idx + 1}</td>
                      <td>
                        <a
                          style={{ cursor: "pointer", color: "blue" }}
                          onClick={() =>
                            handleAfsoClick(item.afso_code, item.afso_name_en)
                          }
                        >
                          {item.afso_name_en}
                        </a>
                      </td>
                      {schemeData.map((scheme) =>
                        verticalHeaders.map((_, i) => {
                          const fieldMap = [
                            "total_rc",
                            "total_units",
                            "fully_ekyc_completed_rc",
                            "fully_ekyc_done_units",
                            "partial_ekyc_done_rc",
                            "partial_ekyc_done_units",
                            "partial_ekyc_not_done_units",
                            "ekyc_not_done_rc",
                            "ekyc_not_done_units",
                          ];
                          return (
                            <td
                              key={`${scheme.schemeid}-${i}`}
                              style={{ textAlign: "right" }}
                            >
                              {item[`${fieldMap[i]}_${scheme.schemeid}`]}
                            </td>
                          );
                        })
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: "bold", backgroundColor: "#f0f0f0" }}>
                    <td colSpan={2} style={{ textAlign: "center" }}>
                      TOTAL
                    </td>
                    {schemeData.map((scheme) =>
                      verticalHeaders.map((_, i) => {
                        const fieldMap = [
                          "total_rc",
                          "total_units",
                          "fully_ekyc_completed_rc",
                          "fully_ekyc_done_units",
                          "partial_ekyc_done_rc",
                          "partial_ekyc_done_units",
                          "partial_ekyc_not_done_units",
                          "ekyc_not_done_rc",
                          "ekyc_not_done_units",
                        ];
                        return (
                          <td
                            key={`${scheme.schemeid}-${i}`}
                            style={{ textAlign: "right" }}
                          >
                            {getSchemeTotals(scheme.schemeid, fieldMap[i])}
                          </td>
                        );
                      })
                    )}
                  </tr>
                </tfoot>
              </table>
              <div style={{ marginTop: "20px", textAlign: "center" }}>
                <button onClick={handleDownloadExcel} className="button button2">
                  Download Excel
                </button>
              </div>
            </div>
          </div>
          <br/><br/>
        </div>        
      ) : (
        <div className="mainhead" align="center">
          No data found
        </div>
      )}
      <div id="ekycCardsUnitsFPSReport">{renderContent}</div>
    </>
  );
};

export default EKYCCardsUnitsMandal;
